import React, { useMemo, useState, useEffect } from 'react';
import { Select } from 'antd';
import optionApi from '../../../services/optionApi';
import { useSelector } from 'react-redux';
import { SkeletonBlock } from '../../skeleton/skeleton';
import { TWButton, TWModal } from '../../ui';

export default function OptionGroupExistModal({
  show,
  onClickOptionGroupExistSubmit,
  optionGroupForm,
  setOptionGroupForm,
  loading,
  setNotf,
  setDialogs,
  storeIds,
}) {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const [optionGroupIds, setOptionGroupIds] = useState([]);
  const [loadingOptionGroupList, setLoadingOptionGroupList] = useState(true);

  const existingSelectedIds = useMemo(() => {
    return (
      optionGroupForm?.productoptiongroups
        ?.filter((x) => x.optiongroup?.isdeleted === false)
        ?.map((x) => x.optiongroup?.id)
        ?.filter(Boolean) || []
    );
  }, [optionGroupForm?.productoptiongroups]);

  const getOptionGroupsList = async () => {
    try {
      setLoadingOptionGroupList(true);
      const storeIdsParams = storeIds.map((id) => `storeids=${id}`).join('&');
      const query = `?companyid=${globalCompany.id}&${storeIdsParams}&pageSize=100`;
      const list = await optionApi.groupList(query);
      if (list.data.data.length > 0) {
        setOptionGroupIds(list.data.data);
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoadingOptionGroupList(false);
    }
  };

  const onFormChange = (val, key) => {
    setOptionGroupForm((prev) => ({ ...prev, [key]: val }));
  };

  const onClose = () => {
    setDialogs((prev) => ({ ...prev, optionGroupExist: false }));
  };

  useEffect(() => {
    if (!show) return;
    getOptionGroupsList();
    setOptionGroupForm((prev) => ({
      ...prev,
      optiongroupids: Array.isArray(prev.optiongroupids)
        ? prev.optiongroupids
        : existingSelectedIds,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <TWModal
      open={show}
      onClose={onClose}
      title="Add Existing Modifier Groups"
      description="Select modifier groups to attach to this product."
      size="lg"
      showCloseButton
      footer={
        <div className="grid grid-cols-2 gap-3">
          <TWButton
            variant="default"
            label="Cancel"
            disabled={loading}
            onClick={onClose}
            className="w-full"
          />
          <TWButton
            variant="primary"
            label="Save"
            disabled={loading}
            loading={loading}
            onClick={onClickOptionGroupExistSubmit}
            className="w-full"
          />
        </div>
      }
    >
      <label
        htmlFor="modifiers"
        className="block text-sm font-medium text-slate-700 dark:text-slate-300"
      >
        Modifiers
      </label>
      <div className="mt-2">
        {loadingOptionGroupList ? (
          <div className="space-y-3 py-2">
            <SkeletonBlock className="h-11 w-full rounded-md" />
            <div className="flex flex-wrap gap-2">
              <SkeletonBlock className="h-7 w-24 rounded-full" />
              <SkeletonBlock className="h-7 w-28 rounded-full" />
              <SkeletonBlock className="h-7 w-20 rounded-full" />
            </div>
          </div>
        ) : (
          <Select
            id="modifiers"
            mode="multiple"
            allowClear
            size="large"
            style={{ width: '100%' }}
            placeholder="Select modifier groups"
            value={optionGroupForm.optiongroupids || existingSelectedIds}
            onChange={(val) => onFormChange(val, 'optiongroupids')}
            options={optionGroupIds?.map((x) => ({
              label: x.name,
              value: x.id,
            }))}
          />
        )}
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          You can select multiple groups. Saving will update links immediately.
        </p>
      </div>
    </TWModal>
  );
}
